import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation.ts';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DealHealthState,
  getHealthScoreColor,
  getHealthScoreGridColumn,
  getHealthScorePopoverTitle,
  getHealthScoreSegmentFillWidth,
  getHealthScoreStateFromScore,
  healthScoreGridTemplateColumns,
  healthScoreSegmentWeights,
} from '../../utils/healthScoreUtils.ts';

interface DealHealthScoreProps {
  value: DealHealthScoreValue;
  maxWidth?: number;
  onOpen?: () => void;
  onReload?: () => void;
  showCurrentState?: boolean;
}

export interface DealHealthScoreValue {
  aiAnalysis: string | null;
  currentScore?: number | null;
  error: boolean;
  healthState: DealHealthState;
  loading: boolean;
  score: number | null;
}

type HealthScoreColor = ReturnType<typeof getHealthScoreColor>;

type HealthScoreBarProps =
  | {
      variant: 'empty';
    }
  | {
      color: HealthScoreColor;
      score: number;
      variant: 'filled';
    };

interface DealHealthScorePopoverProps {
  anchorEl: HTMLElement | null;
  color: HealthScoreColor;
  description: string | null;
  error: boolean;
  loadingCurrentState: boolean;
  loading: boolean;
  onClose: () => void;
  onReload: (event: MouseEvent<HTMLButtonElement>) => void;
  open: boolean;
  score: number;
  showCurrentState: boolean;
  title: string;
}

interface DealHealthScorePopoverBodyProps {
  description: string | null;
  error: boolean;
  loading: boolean;
  onReload: (event: MouseEvent<HTMLButtonElement>) => void;
}

const skeletonLineSx = { borderRadius: 0.5, transform: 'none' } as const;
const currentStateLabelSx = { alignItems: 'center', color: 'text.secondary', display: 'flex', gap: 0.5 } as const;

const HealthScoreBar = (props: HealthScoreBarProps) => {
  const theme = useTheme();

  return (
    <Box
      data-test="deal-health-score"
      sx={{
        display: 'grid',
        gap: theme.spacing(0.25),
        gridTemplateColumns: healthScoreGridTemplateColumns,
        height: 6,
        width: '100%',
      }}
    >
      {healthScoreSegmentWeights.map((weight, segment) => (
        <Box
          key={weight}
          data-test="deal-health-score-segment"
          sx={{
            backgroundColor: theme.palette.action.hover,
            borderRadius: theme.spacing(0.25),
            overflow: props.variant === 'filled' ? 'hidden' : undefined,
          }}
        >
          {props.variant === 'filled' ? (
            <Box
              data-test="deal-health-score-fill"
              sx={{
                backgroundColor: props.color(theme),
                borderRadius: theme.spacing(0.25),
                height: '100%',
                width: getHealthScoreSegmentFillWidth(props.score, segment),
              }}
            />
          ) : null}
        </Box>
      ))}
    </Box>
  );
};

const UnavailableDealHealthScore = ({ maxWidth }: { maxWidth: number }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Box
      data-test="deal-health-score-na"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth,
        width: '100%',
      }}
    >
      <Typography
        component="span"
        data-test="deal-health-score-na-value"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: theme.typography.overline.fontSize,
          fontWeight: theme.typography.fontWeightBold,
          lineHeight: '14px',
          whiteSpace: 'nowrap',
        }}
      >
        {t('N/A')}
      </Typography>
      <HealthScoreBar variant="empty" />
    </Box>
  );
};

const DealHealthScorePopoverBody = ({ description, error, loading, onReload }: DealHealthScorePopoverBodyProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const displayDescription = description ?? '';

  if (loading) {
    return (
      <Box
        data-test="deal-health-score-popover-skeleton"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: 28, width: 188 }}
      >
        <Skeleton
          animation="wave"
          data-test="deal-health-score-popover-skeleton-line"
          height={8}
          sx={skeletonLineSx}
          variant="rounded"
          width={188}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton
            animation="wave"
            data-test="deal-health-score-popover-skeleton-line"
            height={8}
            sx={skeletonLineSx}
            variant="rounded"
            width={110}
          />
          <Skeleton
            animation="wave"
            data-test="deal-health-score-popover-skeleton-line"
            height={8}
            sx={skeletonLineSx}
            variant="rounded"
            width={70}
          />
        </Box>
      </Box>
    );
  }

  const displayError = error || !description?.trim();

  if (displayError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography
          data-test="deal-health-score-error-message"
          variant="caption"
          sx={{ color: theme.palette.text.secondary }}
        >
          {t('Could not load detailed description.')}
        </Typography>
        <Button
          data-test="deal-health-score-reload-button"
          color="primary"
          endIcon={<RefreshIcon sx={{ fontSize: theme.typography.h2.fontSize }} />}
          onClick={onReload}
          size="small"
          sx={{
            alignSelf: 'flex-start',
            fontSize: theme.typography.caption.fontSize,
            fontWeight: theme.typography.fontWeightBold,
            lineHeight: theme.typography.button.lineHeight,
            minWidth: 0,
            px: 0.75,
            py: 0.5,
          }}
        >
          {t('RELOAD')}
        </Button>
      </Box>
    );
  }

  const isLongDescription = displayDescription.length > 120;

  return (
    <Box
      component="div"
      sx={{
        color: theme.palette.text.secondary,
        maxHeight: isLongDescription ? 80 : undefined,
        overflowY: isLongDescription ? 'auto' : undefined,
        paddingRight: isLongDescription ? theme.spacing(0.5) : undefined,
        whiteSpace: 'pre-wrap',
      }}
    >
      <Typography component="span" variant="caption">
        {displayDescription}
      </Typography>
    </Box>
  );
};

const DealHealthScorePopover = ({
  anchorEl,
  color,
  description,
  error,
  loadingCurrentState,
  loading,
  onClose,
  onReload,
  open,
  score,
  showCurrentState,
  title,
}: DealHealthScorePopoverProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(0.25),
            boxShadow: ELEVATION.elevation0,
            overflow: 'hidden',
            width: 228,
          },
        },
      }}
      transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
    >
      <Box data-test="deal-health-score-popover">
        <Box sx={{ backgroundColor: theme.palette.background.paper, px: 2, py: 1.5 }}>
          {loadingCurrentState ? (
            <Box data-test="deal-health-score-current-state-skeleton">
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                <Skeleton height={40} sx={{ transform: 'none' }} variant="rounded" width={40} />
                <Box sx={currentStateLabelSx}>
                  <ScheduleIcon sx={{ fontSize: 16 }} />
                  <Typography variant="overline" sx={{ fontWeight: theme.typography.fontWeightBold, lineHeight: 1 }}>
                    {t('Current State')}
                  </Typography>
                </Box>
              </Box>
              <Skeleton height={14} sx={{ mt: 1, transform: 'none' }} variant="rounded" width={188} />
            </Box>
          ) : (
            <>
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
                <Typography variant="h1" sx={{ color: color(theme) }}>
                  {score}
                </Typography>
                {showCurrentState ? (
                  <Box data-test="deal-health-score-current-state" sx={currentStateLabelSx}>
                    <ScheduleIcon sx={{ fontSize: 16 }} />
                    <Typography variant="overline" sx={{ fontWeight: theme.typography.fontWeightBold, lineHeight: 1 }}>
                      {t('Current State')}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <Typography variant="captionBold" sx={{ color: theme.palette.text.primary }}>
                {title}
              </Typography>
            </>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: theme.palette.background.boxLight,
            borderTop: `1px solid ${theme.palette.divider}`,
            minHeight: loading ? 64 : undefined,
            px: 2,
            pb: loading ? 1.75 : 1.5,
            pt: loading ? 2.75 : 1.5,
          }}
        >
          <DealHealthScorePopoverBody description={description} error={error} loading={loading} onReload={onReload} />
        </Box>
      </Box>
    </Popover>
  );
};

interface DealHealthScoreTriggerProps {
  onOpen?: () => void;
}

const useDealHealthScoreTrigger = ({ onOpen }: DealHealthScoreTriggerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onOpen?.();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return {
    anchorEl,
    handleClick,
    handleClose,
    isPopoverOpen: Boolean(anchorEl),
  };
};

export const DealHealthScore = ({
  maxWidth = 100,
  onOpen,
  onReload,
  value,
  showCurrentState = false,
}: DealHealthScoreProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { aiAnalysis, currentScore, error, healthState, loading, score } = value;
  const roundedScore = score === null ? null : Math.round(score);
  const roundedCurrentScore = currentScore == null ? null : Math.round(currentScore);
  const isScoreAvailable = roundedScore !== null && healthState !== 'na';
  const color = getHealthScoreColor(healthState);
  const trigger = useDealHealthScoreTrigger({ onOpen });
  const hasUsableAiAnalysis = Boolean(aiAnalysis?.trim());
  const popoverLoading = loading || (trigger.isPopoverOpen && Boolean(onOpen) && aiAnalysis === null && !error);

  if (!isScoreAvailable) {
    return <UnavailableDealHealthScore maxWidth={maxWidth} />;
  }

  // Header number: root-cause score in Current State mode, otherwise backend score.
  // Details body always comes from root-cause (`aiAnalysis` + `loading`).
  const headerScore = showCurrentState && roundedCurrentScore !== null ? roundedCurrentScore : roundedScore;
  const headerHealthState =
    showCurrentState && roundedCurrentScore !== null ? getHealthScoreStateFromScore(roundedCurrentScore) : healthState;
  const popoverTitle = getHealthScorePopoverTitle(headerHealthState, t);

  if (!popoverTitle) {
    return <UnavailableDealHealthScore maxWidth={maxWidth} />;
  }

  const handleReload = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onReload?.();
  };

  return (
    <>
      <Box
        aria-label={`${t('Health score')} ${roundedScore}`}
        component="button"
        data-test="deal-health-score-wrapper"
        onClick={trigger.handleClick}
        type="button"
        sx={{
          background: 'transparent',
          border: 0,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'inherit',
          maxWidth,
          padding: 0,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: theme.spacing(0.25),
            gridTemplateColumns: healthScoreGridTemplateColumns,
          }}
        >
          <Typography
            component="span"
            data-test="deal-health-score-value"
            sx={{
              color: color(theme),
              fontSize: theme.typography.overline.fontSize,
              fontWeight: theme.typography.fontWeightBold,
              gridColumn: getHealthScoreGridColumn(roundedScore),
              lineHeight: '14px',
              textAlign: 'center',
            }}
          >
            {roundedScore}
          </Typography>
        </Box>
        <HealthScoreBar color={color} score={roundedScore} variant="filled" />
      </Box>
      <DealHealthScorePopover
        anchorEl={trigger.anchorEl}
        color={getHealthScoreColor(headerHealthState)}
        description={aiAnalysis}
        error={!popoverLoading && (error || !hasUsableAiAnalysis)}
        loadingCurrentState={showCurrentState && loading}
        loading={popoverLoading}
        onClose={trigger.handleClose}
        onReload={handleReload}
        open={trigger.isPopoverOpen}
        score={headerScore}
        showCurrentState={showCurrentState}
        title={popoverTitle}
      />
    </>
  );
};
